/**
 * PIETeamHeatmapCard — manager-only per-report L2/M/L3 heatmap.
 *
 * Cell colours follow the same PE-bucket scheme used elsewhere
 * (Critical / Constrained / Moderate / Strong) so the manager can
 * scan a row in a glance:
 *   • Strong       (≥ 0.70)  — emerald
 *   • Moderate     (≥ 0.50)  — teal
 *   • Constrained  (≥ 0.30)  — amber
 *   • Critical     (< 0.30)  — rose
 *
 * The L3 column shows whether the value came from the MANAGER'S own
 * pulse (most authoritative — the manager is rating their own
 * observation) or from the LEARNER'S self-pulse (fallback when the
 * manager hasn't yet rated this report). Mixed sources within a team
 * are visually distinguished with a tiny pip in the L3 cell.
 *
 * Privacy: only consenting reports show up. No reflection text is
 * ever rendered here.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Grid3x3, ArrowRight, Info } from 'lucide-react';
import { pieApi } from './pieApi';
import logger from '../../lib/logger';
import { usePIEDisplayMode, valueToBucket } from './PIEDisplayMode';

const COLS = [
  { key: 'l2', label: 'L2 · Capability', hint: 'Self-assessed skill across mapped competencies' },
  { key: 'm', label: 'M · Motivation', hint: 'Intrinsic + extrinsic + manager support, weighted' },
  { key: 'l3', label: 'L3 · Behavior', hint: 'Latest pulse — manager-rated when available, otherwise self-rated' },
];

function bucket(score) {
  if (score == null || Number.isNaN(score)) return 'none';
  if (score >= 0.70) return 'strong';
  if (score >= 0.50) return 'moderate';
  if (score >= 0.30) return 'constrained';
  return 'critical';
}

const BUCKET_STYLES = {
  strong:      { bg: 'rgba(16, 185, 129, 0.16)', text: '#047857', border: 'rgba(16, 185, 129, 0.40)' },
  moderate:    { bg: 'rgba(6, 182, 212, 0.16)',  text: '#0E7490', border: 'rgba(6, 182, 212, 0.40)' },
  constrained: { bg: 'rgba(245, 158, 11, 0.16)', text: '#B45309', border: 'rgba(245, 158, 11, 0.40)' },
  critical:    { bg: 'rgba(239, 68, 68, 0.16)',  text: '#B91C1C', border: 'rgba(239, 68, 68, 0.40)' },
  none:        { bg: 'var(--pie-surface)',       text: 'var(--pie-text-muted)', border: 'var(--pie-border)' },
};

export default function PIETeamHeatmapCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    pieApi.trendsTeamHeatmap()
      .then(setData)
      .catch((e) => {
        logger.warn('trendsTeamHeatmap failed', e);
        setErr(true);
      });
  }, []);

  if (err || !data) return null;
  if (!data.is_manager) return null;
  if (!data.rows || data.rows.length === 0) return null;

  return (
    <section
      className="pie-card"
      style={{ marginBottom: 20 }}
      data-testid="pie-team-heatmap-card"
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <h2 className="pie-display-md" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Grid3x3 size={16} aria-hidden style={{ color: 'var(--pie-accent)' }} />
          Team heatmap
        </h2>
        <Link
          to="/mirror/manager-pulse"
          className="pie-link"
          data-testid="pie-team-heatmap-pulse-link"
          style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          Update L3 ratings <ArrowRight size={11} />
        </Link>
      </div>
      <p style={{ fontSize: 13, color: 'var(--pie-text-secondary)', marginBottom: 14, lineHeight: 1.55 }}>
        Each row is one of your direct reports — three cells per row (Capability,
        Motivation, Behavior). Coaching attention usually pays off most where
        Capability is high but Motivation or Behavior aren&apos;t — that&apos;s a
        time/system problem, not a skill gap.
      </p>

      <Legend />

      <div
        style={{ overflowX: 'auto' }}
        data-testid="pie-team-heatmap-table-wrap"
      >
        <table
          className="pie-team-heatmap-table"
          style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 6px', minWidth: 560 }}
          data-testid="pie-team-heatmap-table"
        >
          <thead>
            <tr>
              <th scope="col" style={{ textAlign: 'left', fontSize: 11, color: 'var(--pie-text-muted)', fontWeight: 600, padding: '4px 8px' }}>
                Report
              </th>
              {COLS.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  title={c.hint}
                  style={{ textAlign: 'center', fontSize: 11, color: 'var(--pie-text-muted)', fontWeight: 600, padding: '4px 8px' }}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <HeatmapRow key={row.persona_id} row={row} />
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ fontSize: 11, color: 'var(--pie-text-muted)', marginTop: 10, fontStyle: 'italic' }}>
        L3 cells with a teal pip are sourced from a learner&apos;s self-pulse
        because you haven&apos;t rated them yet — submit a manager pulse to
        replace those with your own observation.
      </p>
    </section>
  );
}

function HeatmapRow({ row }) {
  return (
    <tr data-testid={`pie-team-heatmap-row-${row.persona_id}`}>
      <td style={{ padding: '8px 8px', verticalAlign: 'middle' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--pie-text)' }}>
          {row.name}
        </div>
        <div style={{ fontSize: 11, color: 'var(--pie-text-secondary)' }}>
          {row.role_label}{row.archetype_label ? ` · ${row.archetype_label}` : ''}
        </div>
      </td>
      {COLS.map((c) => {
        const v = row[c.key];
        return (
          <td key={c.key} style={{ padding: '4px 6px' }}>
            <Cell
              value={v}
              isL3FromLearner={c.key === 'l3' && row.l3_source === 'learner_pulse'}
              testid={`pie-team-heatmap-cell-${row.persona_id}-${c.key}`}
            />
          </td>
        );
      })}
    </tr>
  );
}

function Cell({ value, isL3FromLearner, testid }) {
  const b = bucket(value);
  const style = BUCKET_STYLES[b];
  const { mode, thresholds } = usePIEDisplayMode();
  const label =
    value == null
      ? '—'
      : mode === 'verbiage'
      ? valueToBucket(value, thresholds, 'pct01') || '—'
      : `${Math.round(value * 100)}%`;
  return (
    <div
      data-testid={testid}
      data-bucket={b}
      data-display-mode={mode}
      style={{
        position: 'relative',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: 36, borderRadius: 8,
        background: style.bg, color: style.text,
        border: `1px solid ${style.border}`,
        fontSize: mode === 'verbiage' ? 11 : 13, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
        letterSpacing: 0.2,
        padding: mode === 'verbiage' ? '0 6px' : 0,
        textAlign: 'center',
        lineHeight: 1.2,
      }}
    >
      {label}
      {isL3FromLearner && (
        <span
          aria-hidden
          title="Sourced from this report's self-pulse — you haven't rated them yet."
          style={{
            position: 'absolute', top: 4, right: 4,
            width: 6, height: 6, borderRadius: '50%',
            background: 'var(--pie-accent)',
          }}
        />
      )}
    </div>
  );
}

function Legend() {
  const { mode } = usePIEDisplayMode();
  const items = mode === 'verbiage'
    ? [
        { key: 'critical', label: 'Critical' },
        { key: 'constrained', label: 'Constrained' },
        { key: 'moderate', label: 'Moderate' },
        { key: 'strong', label: 'Strong' },
      ]
    : [
        { key: 'critical', label: 'Critical (<30%)' },
        { key: 'constrained', label: 'Constrained (30–49%)' },
        { key: 'moderate', label: 'Moderate (50–69%)' },
        { key: 'strong', label: 'Strong (≥70%)' },
      ];
  return (
    <div
      style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}
      data-testid="pie-team-heatmap-legend"
    >
      {items.map((i) => {
        const s = BUCKET_STYLES[i.key];
        return (
          <div
            key={i.key}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--pie-text-secondary)' }}
          >
            <span
              aria-hidden
              style={{
                width: 14, height: 14, borderRadius: 4,
                background: s.bg, border: `1px solid ${s.border}`,
              }}
            />
            {i.label}
          </div>
        );
      })}
      <div
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--pie-text-muted)', marginLeft: 'auto' }}
      >
        <Info size={11} aria-hidden /> Hover a column header for what it means.
      </div>
    </div>
  );
}
